import { motion } from 'framer-motion';
import { MessageCircle, ThumbsUp, MoreHorizontal } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useNostrProfile } from '../../hooks/useNostrProfile';
import { UserAvatar } from '../Common/UserAvatar';
import { useAuth } from '../../contexts/AuthContext';
import { reactToEvent } from '../../lib/nostr';
import { formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';


interface CompactReplyProps {
    reply: {
        id: string;
        pubkey: string;
        content: string;
        created_at: number;
    };
    isLast?: boolean;
}

/**
 * Compact Reply Component - Reddit/Twitter thread style
 * Minimal vertical space with thread line visual connection
 */
export function CompactReply({ reply, isLast = false }: CompactReplyProps) {
    const { metadata } = useNostrProfile(reply.pubkey);
    const { sk } = useAuth();
    const [isLiking, setIsLiking] = useState(false);
    const [localLikes, setLocalLikes] = useState(0);

    const username = metadata?.display_name || metadata?.name || reply.pubkey.slice(0, 8);
    const isExternal = !metadata;
    const profileLink = `/profile/${reply.pubkey}`;

    const handleLike = async () => {
        if (!sk && !window.nostr) return;
        if (isLiking) return;

        setIsLiking(true);
        try {
            await reactToEvent(reply.id, reply.pubkey, '+', sk);
            setLocalLikes(prev => prev + 1);
        } catch (e) {
            console.error('Like failed:', e);
        } finally {
            setIsLiking(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="group relative"
        >
            {/* Thread Line */}
            {!isLast && (
                <div className="absolute left-5 top-12 bottom-0 w-0.5 bg-surface-200 dark:bg-surface-700" />
            )}

            <div className="flex gap-3 p-3 rounded-xl hover:bg-surface-50 dark:hover:bg-surface-800/50 transition-colors">
                {/* Avatar */}
                <Link to={profileLink} className="flex-shrink-0">
                    <UserAvatar
                        src={metadata?.picture}
                        name={username}
                        size="sm"
                        isExternal={isExternal}
                    />
                </Link>

                {/* Content */}
                <div className="flex-1 min-w-0">
                    {/* Header */}
                    <div className="flex items-center gap-2 mb-1">
                        <Link
                            to={profileLink}
                            className="font-poppins font-medium text-sm text-gray-900 dark:text-white hover:underline"
                        >
                            {username}
                        </Link>
                        <span className="text-xs text-gray-400">·</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            {formatDistanceToNow(reply.created_at * 1000, { addSuffix: true, locale: tr })}
                        </span>
                    </div>

                    {/* Reply Content */}
                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-2 whitespace-pre-wrap break-words">
                        {(() => {
                            // Clean hashtags from content
                            let cleanContent = reply.content;

                            // Remove new format hashtags
                            cleanContent = cleanContent.replace(/\n\n#turkstr #turkce #turkish #mavililer$/m, '');
                            // Remove old format hashtags (backward compatibility)
                            cleanContent = cleanContent.replace(/\n\n#mavililer #mavililersözlük #nostr #türkiye$/m, '');

                            // Remove any trailing hashtags that might be visible
                            cleanContent = cleanContent.replace(/\s*#\w+\s*$/gm, '');

                            return cleanContent.trim();
                        })()}
                    </p>

                    {/* Actions */}
                    <div className="flex items-center gap-4 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                            onClick={handleLike}
                            disabled={isLiking}
                            className="flex items-center gap-1 text-xs text-gray-400 hover:text-pink-500 transition-colors"
                        >
                            <ThumbsUp className="w-3.5 h-3.5" />
                            {localLikes > 0 && <span>{localLikes}</span>}
                        </button>
                        <button className="flex items-center gap-1 text-xs text-gray-400 hover:text-mavi-500 transition-colors">
                            <MessageCircle className="w-3.5 h-3.5" />
                            <span>Yanıtla</span>
                        </button>
                    </div>
                </div>

                {/* More Options */}
                <button className="p-1 opacity-0 group-hover:opacity-100 hover:bg-surface-100 dark:hover:bg-surface-700 rounded transition-all h-fit">
                    <MoreHorizontal className="w-4 h-4 text-gray-400" />
                </button>
            </div>
        </motion.div>
    );
}
